import React, { useState, useEffect, useRef } from 'react'
import { useMutation } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'
import { Select, SelectOption } from './Select'
import { selectUserToken } from '../store/user'
import { carrierClient } from '../clients/CarrierClient'
import { TestingKey } from '../util/testing'

type Props = {
    placeholder?: string | null
    value?: string | null
    onChange: (nextId: string | null, nextLabel: string | null | undefined) => void
    onSubmit?: () => void
    label?: string
    hasDanger?: boolean
    errorMessage?: string
    disabled?: boolean
    testingKeys?: [TestingKey] | [TestingKey, string]
}

export const ConnectedCarrierSelect = (props: Props): JSX.Element => {
    const cancelRef = useRef<(() => void) | null>(null)
    const {
        value,
        label,
        hasDanger,
        errorMessage,
        placeholder: providedPlaceholder,
        disabled,
        testingKeys,
        onChange,
        onSubmit,
    } = props
    const [options, setOptions] = useState<SelectOption[]>([])
    const token = useSelector(selectUserToken, shallowEqual)

    const { mutateAsync: queryCarrierList, status: listStatus } = useMutation(
        'carrier-select',
        carrierClient.getCarriers
    )

    const {
        data: selectedCarrier,
        mutateAsync: getSingleCarrier,
        reset,
        status: carrierItemStatus,
    } = useMutation('carrier-item', carrierClient.getCarrierById)

    useEffect(() => {
        queryOptions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const getItem = (): void => {
            if (!token) return
            if (!value) {
                reset()
                return
            }
            getSingleCarrier({ token, carrierId: value })
        }
        getItem()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, value])

    const queryOptions = async (nextQuery?: string | null): Promise<void> => {
        if (!token) return
        const nextCarriers = await queryCarrierList({ token, search: nextQuery || undefined })
        // const nextOptions = Object.entries(nextCarriers).map(([id, carrier]) => ({
        //     value: id,
        //     label: carrier.name
        // }))
        const nextOptions = []
        setOptions(nextOptions)
    }

    const isLoading = listStatus === 'loading' || carrierItemStatus === 'loading'
    const hasErrorFetchingData = listStatus === 'error' || carrierItemStatus === 'error'
    let placeholder = providedPlaceholder || 'Select...'
    if (isLoading) placeholder = 'loading...'
    if (hasErrorFetchingData) placeholder = 'Something went wrong...'
    return (
        <Select
            label={label}
            testingKeys={testingKeys}
            isLoading={isLoading}
            placeholder={placeholder}
            hasDanger={hasDanger}
            error={errorMessage}
            options={options}
            disabled={disabled}
            onChange={onChange}
            onEnterPress={onSubmit}
            noOptionsMessage={isLoading ? 'Loading...' : 'No Carriers'}
            onSearch={async (nextValue) => {
                // TODO: make a lil more optimal ... maybe add a debounce
                if (cancelRef.current) {
                    cancelRef.current()
                    cancelRef.current = null
                }

                cancelRef.current = () => reset()
                await queryOptions(nextValue)
                cancelRef.current = null
            }}
            value={selectedCarrier?.name}
        />
    )
}
